import { Menu } from 'lucide-react';
import React, { createContext, Suspense, useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import { useClients } from 'src/context/ClientContext';
import { useProfile } from 'src/context/ProfileContext';
import { useSubscription } from 'src/context/SubscriptionContext';
import { useTheme } from 'src/context/ThemeContext';
import LockedAccountScreen from 'src/shared/components/LockedAccountScreen';
import MaintenanceBanner from 'src/shared/components/MaintenanceBanner';
import SessionLoadingScreen from 'src/shared/components/SessionLoadingScreen';
import UpgradeCelebrationScreen from 'src/shared/components/UpgradeCelebrationScreen';
import WelcomeExperienceScreen from 'src/shared/components/WelcomeExperienceScreen';
import { ContainerBox, IconButton, LoadingSpinner } from 'src/tef/components';
import { sessionManager } from 'src/utils/sessionManager';

import DashboardSidebar from './components/DashboardSidebar';
import { SidebarProvider, useSidebar } from './context/SidebarContext';

const AppReadyContext = createContext(false);
export function useAppReady() {
  return useContext(AppReadyContext);
}

const Dashboard = React.lazy(() => import('./Dashboard'));
const ResearchViewer = React.lazy(() => import('./ResearchViewer'));
const VaultDocumentViewer = React.lazy(() => import('./VaultDocumentViewer'));
const UploadDocument = React.lazy(() => import('./UploadDocument'));
const NewResearch = React.lazy(() => import('src/pages/new-research/NewResearch'));
const NewClient = React.lazy(() => import('src/pages/new-client/NewClient'));
const Settings = React.lazy(() => import('src/pages/settings/Settings'));
const UpdateResearchFinalDetails = React.lazy(() => import('src/pages/update-research/UpdateResearchFinalDetails'));
const HelpPage = React.lazy(() => import('src/pages/help/HelpPage'));

function AppRoutes() {
  return (
    <ContainerBox className="w-full h-full min-h-0 flex flex-col overflow-y-auto">
      <Suspense
        fallback={
          <ContainerBox className="flex-1 flex items-center justify-center">
            <LoadingSpinner variant={{ size: 'lg' }} />
          </ContainerBox>
        }
      >
        <Routes>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="client/:clientId" element={<Dashboard />} />
          <Route path="research/:researchId" element={<ResearchViewer />} />
          <Route path="vault-document/:documentId" element={<VaultDocumentViewer />} />
          <Route path="settings" element={<Settings />} />
          <Route path="new-research" element={<NewResearch />} />
          <Route path="new-client" element={<NewClient />} />
          <Route path="upload-document" element={<UploadDocument />} />
          <Route path="update-research-finalize" element={<UpdateResearchFinalDetails />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </ContainerBox>
  );
}

function MobileMenuButton() {
  const { setMobileOpen } = useSidebar();

  return (
    <ContainerBox data-tutorial="mobile-menu" className="absolute top-3 left-3 z-40 pt-[env(safe-area-inset-top)]">
      <IconButton
        icon={<Menu className="w-5 h-5" />}
        variant={{ style: 'ghost', size: 'sm' }}
        onClick={() => setMobileOpen(true)}
        aria-label="Open menu"
        className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm shadow-sm rounded-lg"
      />
    </ContainerBox>
  );
}

export { MobileMenuButton };

function MainLayoutContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const { profile, loading: profileLoading, updateProfile } = useProfile();
  const { loading: clientsLoading } = useClients();
  const { plan, subscription, loading: subLoading, refreshSubscription } = useSubscription();
  const [dismissed, setDismissed] = useState(false);
  const [showWelcome, setShowWelcome] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const contextsReady = !profileLoading && !clientsLoading;
  const hasSeenWelcome = profile?.custom_fields?.has_seen_welcome === true;

  useEffect(() => {
    if (contextsReady && profile && !hasSeenWelcome && !showWelcome) {
      setShowWelcome(plan?.name ?? 'plan_unknown');
    }
  }, [contextsReady, profile, hasSeenWelcome, plan?.name, showWelcome]);

  useEffect(() => {
    if (searchParams.get('upgrade') === 'success') {
      setShowUpgrade(true);
      const next = new URLSearchParams(searchParams);
      next.delete('upgrade');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const stage = contextsReady ? 'ready' : 'loading';

  const handleFadeComplete = useCallback(() => setDismissed(true), []);

  const handleWelcomeComplete = useCallback(async () => {
    setShowWelcome(null);
    setDismissed(true);
    await updateProfile({ custom_fields: { ...profile?.custom_fields, has_seen_welcome: true } });
  }, [updateProfile, profile?.custom_fields]);

  const handleUpgradeComplete = useCallback(() => {
    setShowUpgrade(false);
    refreshSubscription();
  }, [refreshSubscription]);

  const handleLogout = async () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    try {
      await sessionManager.clearSession();
    } catch {
      localStorage.removeItem('session_start');
    }
    navigate('/login', { replace: true });
  };

  const FULLSCREEN_BLOCKED_STATUSES = ['locked', 'trial_expired', 'pending_payment'];
  const isFullScreenBlocked =
    !subLoading && subscription?.status && FULLSCREEN_BLOCKED_STATUSES.includes(subscription.status);

  if (isFullScreenBlocked) {
    return <LockedAccountScreen />;
  }

  const appReady = dismissed && !showWelcome && !showUpgrade;

  return (
    <AppReadyContext.Provider value={appReady}>
      <ContainerBox className={`h-screen flex flex-col ${theme.colors.background}`}>
        {showWelcome && <WelcomeExperienceScreen planType={showWelcome} onComplete={handleWelcomeComplete} />}
        {showUpgrade && <UpgradeCelebrationScreen onComplete={handleUpgradeComplete} />}
        {!dismissed && !showWelcome && !showUpgrade && (
          <SessionLoadingScreen stage={stage} planName={plan?.name} onFadeComplete={handleFadeComplete} />
        )}
        <MaintenanceBanner />
        <ContainerBox className="flex-1 flex overflow-hidden">
          <DashboardSidebar onLogout={handleLogout} />
          <ContainerBox as="main" className={`flex-1 overflow-hidden ${theme.colors.background}`}>
            <AppRoutes />
          </ContainerBox>
        </ContainerBox>
      </ContainerBox>
    </AppReadyContext.Provider>
  );
}

export default function MainLayout() {
  return (
    <SidebarProvider>
      <MainLayoutContent />
    </SidebarProvider>
  );
}
